(() => {
  if (window.bauvenDrawerRedesignLoaded) return;
  window.bauvenDrawerRedesignLoaded = true;
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  let scrollTop = 0;
  let scheduled = false;
  let observedDrawer;
  const drawer = () => document.querySelector('cart-drawer.bauven-redesign');
  const observer = new MutationObserver(() => {
    if (scheduled || busy) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; sync(); });
  });

  function sync() {
    const current = drawer();
    if (!current) return;
    if (current !== observedDrawer) {
      observer.disconnect();
      observer.observe(current, {childList: true, subtree: true});
      observedDrawer = current;
    }
    for (const card of current.querySelectorAll('.br-addon')) {
      if (card.dataset.brNeedsRefresh === 'true') { card.querySelector('[data-br-action]').disabled = true; continue; }
      const selected = card.dataset.cartSelected === 'true';
      card.dataset.selected = String(selected);
      if (selected) card.dataset.id = card.dataset.cartVariant;
      const button = card.querySelector('[data-br-action]');
      button.setAttribute('aria-pressed', String(selected));
      button.disabled = !selected && card.dataset.available !== 'true';
      const select = card.querySelector('[data-br-variant]');
      const input = card.querySelector('[name="id"]');
      if (input) input.value = card.dataset.id;
      if (select) {
        if ([...select.options].some(option => option.value === card.dataset.id)) select.value = card.dataset.id;
        select.disabled = selected;
        if (!selected) updateVariant(card, select);
      }
      button.disabled = !selected && card.dataset.available !== 'true';
    }
    for (const badge of current.querySelectorAll('[data-br-payment-label]')) {
      const svg = badge.querySelector('svg');
      const title = svg?.querySelector('title')?.textContent?.trim() || '';
      if (svg && (!title || /^(unknown|payment(?: icon)?|generic(?: card)?|credit card)$/i.test(title))) {
        badge.textContent = badge.dataset.brPaymentLabel;
      }
    }
    const body = current.querySelector('.br-body');
    if (body && Math.abs(body.scrollTop - scrollTop) > 1) body.scrollTop = scrollTop;
  }

  function updateVariant(card, select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    card.dataset.id = option.value;
    card.dataset.available = String(!option.disabled);
    card.querySelector('[name="id"]').value = option.value;
    const price = card.querySelector('.regular-price');
    if (price && option.dataset.brPrice && price.textContent !== option.dataset.brPrice) price.textContent = option.dataset.brPrice;
    const compare = card.querySelector('.compare-price');
    if (compare) {
      compare.hidden = option.dataset.brDiscounted !== 'true';
      if (option.dataset.brNormal && compare.textContent !== option.dataset.brNormal) compare.textContent = option.dataset.brNormal;
    }
    const offer = card.querySelector('.br-addon-offer');
    if (offer) {
      offer.hidden = option.dataset.brDiscounted !== 'true';
      if (option.dataset.brOffer && offer.textContent !== option.dataset.brOffer) offer.textContent = option.dataset.brOffer;
    }
    const plan = card.querySelector('[name="selling_plan"]');
    if (plan && option.dataset.brPlan) plan.value = option.dataset.brPlan;
  }

  async function request(path, body) {
    const response = await fetch(root + path, {
      method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body)
    });
    let data;
    try { data = await response.json(); }
    catch { throw new Error('We could not update your basket. Please try again.'); }
    if (!response.ok || data.errors || data.status >= 400) throw new Error(data.description || data.message || 'This item could not be updated. Please try again.');
    return data;
  }

  async function sectionsFor(data) {
    if (sectionIds.every(id => typeof data.sections?.[id] === 'string')) return data;
    const response = await fetch(root + 'cart?sections=' + sectionIds.join(','), {credentials: 'same-origin', cache: 'no-store'});
    if (!response.ok) throw new Error('Your basket changed. Reload to see the latest contents.');
    const sections = await response.json();
    if (!sectionIds.every(id => typeof sections[id] === 'string')) throw new Error('Your basket changed. Reload to see the latest contents.');
    return {...data, sections};
  }

  async function toggle(card) {
    const current = drawer();
    if (!current || busy || current.querySelector('.cart__items--disabled')) return;
    const selected = card.dataset.cartSelected === 'true';
    const slot = card.dataset.brSlot;
    const body = current.querySelector('.br-body');
    scrollTop = body?.scrollTop || 0;
    const error = current.querySelector('#BrAddonError');
    if (error) { error.hidden = true; error.textContent = ''; }
    busy = true;
    const controls = [...current.querySelectorAll('button,input,select')].filter(control => !control.disabled && !control.classList.contains('br-close'));
    controls.forEach(control => { control.disabled = true; });
    body?.setAttribute('aria-busy', 'true');
    card.dataset.brBusy = 'true';
    let confirmed = false;
    try {
      const common = {sections: sectionIds, sections_url: window.location.pathname};
      let data;
      if (selected) {
        if (!card.dataset.lineKey) throw new Error('Reload your basket, then try removing this item again.');
        data = await request('cart/change.js', {...common, id: card.dataset.lineKey, quantity: 0});
      } else {
        const item = {id: Number(card.dataset.id), quantity: 1};
        const plan = card.querySelector('[name="selling_plan"]')?.value;
        if (plan) item.selling_plan = Number(plan);
        data = await request('cart/add.js', {...common, items: [item]});
      }
      confirmed = true;
      data = await sectionsFor(data);
      const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
      const fresh = parsed.querySelector('cart-drawer');
      if (!fresh || !parsed.querySelector('#CartDrawer')) throw new Error('Your basket changed. Reload to see the latest contents.');
      // Preserve the outer custom element and use the theme's section renderer.
      card.dataset.selected = String(!selected);
      current.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      current.renderContents(data, true);
    } catch (failure) {
      card.dataset.selected = String(confirmed ? !selected : selected);
      const target = drawer()?.querySelector('#BrAddonError');
      if (target) {
        target.textContent = confirmed ? 'Your basket changed. Reload to see the latest contents.' :
          failure instanceof TypeError ? 'We could not connect. Please try again.' : failure.message;
        target.hidden = false;
      }
      // A confirmed mutation is never repeated, even if section rendering fails.
      if (confirmed) card.dataset.brNeedsRefresh = 'true';
    } finally {
      busy = false;
      controls.forEach(control => { if (control.isConnected) control.disabled = false; });
      body?.removeAttribute('aria-busy');
      delete card.dataset.brBusy;
      if (card.dataset.brNeedsRefresh !== 'true') sync();
      else card.querySelector('[data-br-action]').disabled = true;
      const nextButton = [...(drawer()?.querySelectorAll('.br-addon') || [])].find(node => node.dataset.brSlot === slot)?.querySelector('[data-br-action]');
      if (nextButton) nextButton.focus({preventScroll: true});
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-br-action]');
    if (!button || !button.closest('cart-drawer.bauven-redesign')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!button.disabled) toggle(button.closest('.br-addon'));
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-br-variant]') || !event.target.closest('cart-drawer.bauven-redesign')) return;
    event.stopImmediatePropagation();
    if (!busy) updateVariant(event.target.closest('.br-addon'), event.target);
  }, true);
  document.addEventListener('scroll', event => {
    if (!busy && event.target.matches?.('.bauven-redesign .br-body')) scrollTop = event.target.scrollTop;
  }, true);
  document.addEventListener('shopify:section:load', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, {once: true});
  else sync();
})();
